import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Info, Mic, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './FeedbackForm.css';

const HOOKS_COUNT = 12;

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

// Hard cap so a forgotten recording can't grow unbounded (huge upload + OpenRouter
// cost). At the cap we auto-send whatever was captured.
const MAX_RECORD_SECONDS = 180;

// The rating form shared by translations and the QA validator: stars, what went
// wrong, a voice note or a comment. The caller decides where it is sent.
// `thanks(categories)` words the message shown once sent; by default a plain thank you.
export default function FeedbackForm({ categories, categoryPrefix, onSubmit, onVoice, thanks }) {
  const { t, i18n } = useTranslation('chat');
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Voice feedback: record in the browser (keeps recording when the tab is
  // hidden), then submit the audio - the backend transcribes it in the
  // background and fills in the comment.
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);
  const voiceSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window !== 'undefined' &&
    !!window.MediaRecorder;

  const firstName = useMemo(() => {
    const name = user?.name || '';
    return name.split(/\s+/)[0] || 'Friend';
  }, [user?.name]);

  const hookIndex = useMemo(() => Math.floor(Math.random() * HOOKS_COUNT), []);
  const hook = t(`feedback.hooks.${hookIndex}`, { name: firstName });

  const handleStarClick = (selectedRating) => {
    setRating(selectedRating);
    setShowForm(true);
  };

  const submitFeedback = async (ratingValue, commentValue = null, categoriesValue = null) => {
    setSubmitting(true);
    try {
      await onSubmit({ rating: ratingValue, comment: commentValue, categories: categoriesValue });
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert(t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await submitFeedback(
      rating,
      comment || null,
      selectedCategories.length > 0 ? selectedCategories : null
    );
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      recorderRef.current = rec;
      rec.start();
      startTimeRef.current = Date.now();
      setElapsed(0);
      setRecording(true);
      // Compute elapsed from the real start time so the duration stays correct
      // even if the tab is backgrounded (interval ticks get throttled there).
      timerRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsed(secs);
        if (secs >= MAX_RECORD_SECONDS) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          sendVoice();
        }
      }, 500);
    } catch (err) {
      alert(t('feedback.voice.micDenied'));
    }
  };

  const cancelRecording = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.onstop = null;
      rec.stop();
    }
    chunksRef.current = [];
    setRecording(false);
    stopStream();
  };

  // Stop recording and submit the captured audio. onstop fires after the last
  // chunk lands, so we build the blob and post from there.
  const sendVoice = () => {
    const rec = recorderRef.current;
    if (!rec) return;
    setVoiceBusy(true);
    rec.onstop = async () => {
      stopStream();
      setRecording(false);
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
      chunksRef.current = [];
      if (!blob.size) {
        setVoiceBusy(false);
        return;
      }
      try {
        await onVoice({ rating, categories: selectedCategories, blob, language: i18n.language });
        setSubmitted(true);
      } catch (error) {
        console.error('Failed to submit voice feedback:', error);
        alert(t('error'));
      } finally {
        setVoiceBusy(false);
      }
    };
    rec.stop();
  };

  // Clean up the mic if the form unmounts mid-recording.
  useEffect(() => () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    stopStream();
  }, []);

  if (submitted) {
    return (
      <div className="document-feedback submitted">
        <span className="thank-you-message">{thanks ? thanks(selectedCategories) : t('feedback.thankYou')}</span>
      </div>
    );
  }

  return (
    <div className="document-feedback">
      <div className="feedback-prompt-row">
        <span className="feedback-hook">
          {hook}
          <span
            className="feedback-info-icon"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <Info size={16} />
            {showTooltip && (
              <div className="feedback-tooltip">
                {t('feedback.tooltip')}
              </div>
            )}
          </span>
        </span>

        <div className="star-rating">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              className={`star-btn ${star <= (hoveredRating || rating) ? 'active' : ''}`}
              onClick={() => handleStarClick(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              disabled={submitting}
              type="button"
            >
              <Star
                size={32}
                fill={star <= (hoveredRating || rating) ? 'currentColor' : 'none'}
              />
            </button>
          ))}
        </div>
      </div>

      {showForm && (
        <form className="feedback-form" onSubmit={handleFormSubmit}>
          <div className="category-tags">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                className={`category-tag ${selectedCategories.includes(category) ? 'selected' : ''}`}
                onClick={() => toggleCategory(category)}
              >
                {t(`${categoryPrefix}.${category}`)}
              </button>
            ))}
          </div>

          {voiceSupported && (
            <div className="feedback-voice">
              {!recording ? (
                <button
                  type="button"
                  className="feedback-record-btn"
                  onClick={startRecording}
                  disabled={voiceBusy || submitting}
                >
                  <span className="rec-eq" aria-hidden="true">
                    <i /><i /><i /><i /><i />
                  </span>
                  <Mic size={18} />
                  <span>{voiceBusy ? t('feedback.voice.processing') : t('feedback.voice.record')}</span>
                </button>
              ) : (
                <div className="feedback-recording">
                  <span className="rec-eq live" aria-hidden="true">
                    <i /><i /><i /><i /><i /><i /><i />
                  </span>
                  <span className="rec-timer">{fmtTime(elapsed)}</span>
                  <span className="rec-spacer" />
                  <button type="button" className="rec-cancel" onClick={cancelRecording} title={t('feedback.voice.cancel')}>
                    <X size={16} />
                  </button>
                  <button type="button" className="rec-send" onClick={sendVoice} disabled={voiceBusy}>
                    {t('feedback.voice.send')}
                  </button>
                </div>
              )}
            </div>
          )}

          <textarea
            className="feedback-comment"
            placeholder={t('feedback.comment.placeholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
          />

          <button
            type="submit"
            className="btn btn-primary submit-feedback-btn"
            disabled={submitting || recording}
          >
            {submitting ? t('loading') : t('feedback.submit')}
          </button>
        </form>
      )}
    </div>
  );
}

